import { Box, Divider, Skeleton, Stack, useMantineTheme } from '@mantine/core';
import { useDebouncedState } from '@mantine/hooks';
import type { SelectablePropertyItem } from '@repo/common/components/SingleSelector/types';
import { EntityType } from '@repo/common/enums/entityType';
import { pluralize } from '@repo/common/utils';
import { Badge } from '@repo/foundations';
import { every, isEmpty, isNil, noop, uniq } from 'lodash-es';
import { useCallback, useState } from 'react';
import type { ISecodaEntity } from '../../api';
import { ALWAYS_PUBLISHED_ENTITIES, useSearch } from '../../api';
import SearchBox from '../SearchBox/SearchBox';
import { closeSpotlight } from '../Spotlight';
import { ActionCheckbox } from './ActionCheckbox';
import type { ActionProperty } from './types';
import { RESOURCE_LABEL } from './types';

interface ActionModalProps<T extends ISecodaEntity> {
	defaultSelected: T[];
	property: ActionProperty;
	single: boolean;
	withSearch?: Record<string, string>;
	withLocalProperties?: SelectablePropertyItem[];
	bulkUpdateSecodaEntities: any;
}

export function ActionModal<T extends ISecodaEntity>({
	property,
	single,
	defaultSelected,
	withLocalProperties,
	withSearch,
	bulkUpdateSecodaEntities,
}: ActionModalProps<T>) {
	const [selected, setSelected] = useState<T[]>(defaultSelected);

	const theme = useMantineTheme();

	const [debouncedSearch, setDebouncedSearch] = useDebouncedState<string>(
		'',
		withSearch ? 300 : 10
	);

	const { isFetching, data: parentData } = useSearch({
		searchTerm: debouncedSearch,
		filters: withSearch,
		options: {
			enabled: !withLocalProperties,
			select: ({ results }) =>
				results
					?.filter(
						(r) =>
							!selected.map((s) => s.id).includes(r.id) &&
							r.entity_type === selected?.[0].entity_type
					)
					.map(
						({ id, title }) =>
							({
								label: title,
								value: id,
							}) as SelectablePropertyItem
					),
		},
	});

	const handleSearch = useCallback(
		(searchTerm: string) => {
			setDebouncedSearch(searchTerm);
		},
		[setDebouncedSearch]
	);

	const isIndeterminate = useCallback(
		(value: string | boolean) => {
			if (single) {
				return selected.some(
					(entity) => entity[property as keyof ISecodaEntity] === value
				);
			} else {
				return selected.some((entity) =>
					(entity[property as keyof ISecodaEntity] as string[]).includes(
						String(value)
					)
				);
			}
		},
		[property, selected, single]
	);

	const isChecked = useCallback(
		(value: string | boolean) => {
			if (single) {
				return every(
					selected,
					(entity) => entity[property as keyof ISecodaEntity] === value
				);
			} else {
				return selected.every((entity) =>
					(entity[property as keyof ISecodaEntity] as string[]).includes(
						String(value)
					)
				);
			}
		},
		[property, selected, single]
	);

	const items = withLocalProperties ? withLocalProperties : parentData;
	const actionIconType =
		property === 'owners' || property === 'owners_groups' ? 'avatar' : 'none';

	const handleTrigger = useCallback(
		async (value: string | boolean) => {
			const filteredEntities = selected.filter((entity) => {
				// For published status, exclude column entities and always published entities
				if (property === 'published') {
					return (
						entity.entity_type !== EntityType.column &&
						!ALWAYS_PUBLISHED_ENTITIES.includes(entity.entity_type)
					);
				}
				return true;
			});

			if (filteredEntities.length === 0) {
				return;
			}

			const everyChecked =
				!single &&
				every(filteredEntities, (entity) =>
					(entity[property as keyof ISecodaEntity] as string[]).includes(
						value as string
					)
				);

			const data = filteredEntities.map((select: T) => {
				const computePropertyValue = () => {
					if (single) {
						return value;
					} else {
						if (
							// Always add the value if it's not present *in all* selected entities.
							everyChecked
						) {
							return (
								select[property as keyof ISecodaEntity] as string[]
							).filter((item) => item !== value);
						} else {
							return uniq([
								...((select[property as keyof ISecodaEntity] ??
									[]) as string[]),
								value as string,
							]);
						}
					}
				};

				const newValue = computePropertyValue();

				setSelected((prevSelected) => {
					const updatedSelected = prevSelected.map((prev) => {
						if (prev.id === select.id) {
							return {
								...prev,
								[property]: newValue,
							};
						}
						return prev;
					});
					return updatedSelected;
				});

				return {
					id: select.id,
					data: {
						[property]: newValue,
					},
				};
			});

			await bulkUpdateSecodaEntities(
				{
					data,
				},
				{
					onSuccess: () => {
						closeSpotlight('bulkActions');
					},
				}
			);
		},
		[bulkUpdateSecodaEntities, property, selected, single]
	);

	return (
		<Box m={`-${theme.spacing.sm}`}>
			<Badge
				ml={theme.spacing.sm}
				mb={0}
				variant="neutral"
				size="sm"
				sx={{
					textTransform: 'lowercase',
				}}
			>
				{selected.length}{' '}
				{selected.length > 1 ? pluralize(RESOURCE_LABEL) : RESOURCE_LABEL}
			</Badge>
			{property !== 'pii' &&
				property !== 'verified' &&
				property !== 'published' && (
					<>
						<SearchBox
							size="md"
							icon={undefined}
							className={undefined}
							classNames={undefined}
							variant="default"
							placeholder={'Search...'}
							onlySearchOnEnter={false}
							onSearch={handleSearch}
							onCancelSearch={noop}
							defaultSearchTerm=""
							autoFocus={!isEmpty(debouncedSearch)}
							sx={{
								input: {
									boxShadow: 'none',
									border: 'none',
								},
								'input:focus': {
									border: 'none',
									boxShadow: 'none',
								},
							}}
						/>
						<Divider mx={`-${theme.spacing.md}`} />
					</>
				)}
			{(isNil(withSearch) || !isEmpty(debouncedSearch)) && (
				<Stack mx={`${-theme.spacing.sm}`} mt={theme.spacing.xs} spacing={0}>
					{isFetching && <Skeleton h={theme.other.space[80]} />}
					{!isFetching &&
						items
							?.filter((item) =>
								item.label
									?.toLowerCase()
									.includes(debouncedSearch?.toLowerCase())
							)
							?.map((item) => (
								<ActionCheckbox
									key={`${item.label}-${item.value}`}
									handleTrigger={handleTrigger}
									item={item}
									iconType={actionIconType}
									state={
										// eslint-disable-next-line no-nested-ternary
										isChecked(item.value)
											? 'checked'
											: isIndeterminate(item.value)
												? 'indeterminate'
												: 'unchecked'
									}
								/>
							))}
				</Stack>
			)}
		</Box>
	);
}
